import { useState, useEffect, useCallback, useMemo, Fragment } from 'react'
import { supabase } from '../lib/supabase'
import { useProjectAuth } from './useProjectAuth'
import { getCached, setCached, invalidate } from '../lib/queryCache'
import * as XLSX from 'xlsx'

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)
const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
)
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-4 h-4">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
    <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35" strokeLinecap="round"/>
  </svg>
)
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const Toast = ({ message, type, onClose }) => (
  <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl text-sm font-medium
    ${type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
    {type === 'success' ? <CheckIcon /> : <XIcon />}
    <span>{message}</span>
    <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100"><XIcon /></button>
  </div>
)

const fmt = (n) => n ? Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'
const fmtN = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const packageColors = [
  'bg-[#D1D5DB] text-[#1A7A4A] border-[#1A7A4A]/30',
  'bg-emerald-100 text-emerald-700 border-emerald-200',
  'bg-[#D1D5DB] text-[#0F1017] border-purple-200',
  'bg-amber-100 text-amber-700 border-amber-200',
  'bg-rose-100 text-rose-700 border-rose-200',
  'bg-cyan-100 text-cyan-700 border-cyan-200',
  'bg-[#D1D5DB] text-[#1A7A4A] border-indigo-200',
  'bg-orange-100 text-orange-700 border-orange-200',
]


function TenderDirectTab({ projectId, items, loading, uploadPreview, uploading, onFileSelect, onConfirmUpload, onCancelUpload,
  selected, onToggle, onToggleAll, onDeleteSelected, viewMode, onToggleView,
  editItem, editForm, onEdit, onEditChange, onEditSave, onEditCancel, analysisCodes, subCodes = [], can }) {
  const fmtN = n => Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})

  const activityItems = items.filter(i => i.row_type === 'activity' || (!i.is_package_header && !['material','labour','subcontract','equipment'].includes(i.row_type)))
  const totalBudget = activityItems.reduce((s,i) => s+(parseFloat(i.total_budget)||0), 0)
  const totalMat    = activityItems.reduce((s,i) => s+(parseFloat(i.material_total)||0), 0)
  const totalLab    = activityItems.reduce((s,i) => s+(parseFloat(i.labour_total)||0), 0)
  const totalSub    = activityItems.reduce((s,i) => s+(parseFloat(i.subcontractor_total)||0), 0)
  const totalEqp    = activityItems.reduce((s,i) => s+(parseFloat(i.equipment_total)||0), 0)

  // Group by package preserving order
  const packages = {}
  let lastPkg = null
  for (const item of items) {
    if (item.is_package_header || item.row_type === 'package') {
      lastPkg = item.description
      packages[lastPkg] = { header: item, activities: [] }
    } else if (item.row_type === 'activity') {
      if (lastPkg && packages[lastPkg]) packages[lastPkg].activities.push({ ...item, resources: [] })
      else { if (!packages['__ungrouped']) packages['__ungrouped'] = { header: null, activities: [] }; packages['__ungrouped'].activities.push({ ...item, resources: [] }) }
    } else if (['material','labour','subcontract','equipment'].includes(item.row_type)) {
      // attach resource to last activity in current package
      const pkg = packages[lastPkg] || packages['__ungrouped']
      if (pkg && pkg.activities.length > 0) {
        pkg.activities[pkg.activities.length - 1].resources.push(item)
      }
    }
  }

  const RES_COLORS = {
    material:    { bg: 'bg-blue-50',   text: 'text-blue-700',   label: 'Material' },
    labour:      { bg: 'bg-green-50',  text: 'text-green-700',  label: 'Labour' },
    subcontract: { bg: 'bg-orange-50', text: 'text-orange-700', label: 'Subcontract' },
    equipment:   { bg: 'bg-amber-50',  text: 'text-amber-700',  label: 'Equipment' },
  }

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-5 gap-3">
        {[
          { label: 'Total Budget', val: totalBudget, color: 'text-[#0F1017]' },
          { label: 'Material',     val: totalMat,    pct: totalBudget ? totalMat/totalBudget*100 : 0, color: 'text-blue-600' },
          { label: 'Labour',       val: totalLab,    pct: totalBudget ? totalLab/totalBudget*100 : 0, color: 'text-green-600' },
          { label: 'Subcontract',  val: totalSub,    pct: totalBudget ? totalSub/totalBudget*100 : 0, color: 'text-orange-600' },
          { label: 'Equipment',    val: totalEqp,    pct: totalBudget ? totalEqp/totalBudget*100 : 0, color: 'text-amber-600' },
        ].map(k => (
          <div key={k.label} className="bg-[#F3F4F6] rounded-2xl border border-[#E5E7EB] p-4">
            <p className="text-xs text-[#9CA3AF] uppercase font-semibold mb-1">{k.label}</p>
            <p className={`text-lg font-bold font-mono ${k.color}`}>{fmtN(k.val)}</p>
            {k.pct !== undefined && <p className="text-xs text-[#9CA3AF] mt-0.5">{k.pct.toFixed(1)}%</p>}
          </div>
        ))}
      </div>

      {/* Upload */}
      {!uploadPreview ? (
        <div className="bg-[#F3F4F6] rounded-2xl border-2 border-dashed border-[#E5E7EB] p-8 text-center">
          <p className="text-[#9CA3AF] mb-3 text-sm">ارفع Excel الـ Tender Direct (v3 format)</p>
          <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-[#0F1017] text-white rounded-lg text-sm hover:bg-[#2E2C29]">
            📤 اختر ملف
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={e => { if (e.target.files[0]) onFileSelect(e.target.files[0]) }} />
          </label>
          <p className="text-xs text-[#9CA3AF] mt-2">Columns: Item No. | Description | Resource Description | Cost Code | Resource | Code | Qty | Unit | Rate | Total | Resource Code (اختياري، عمود جديد بعد Total)</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-semibold text-[#0F1017]">معاينة — {uploadPreview.length} بند</span>
            <div className="flex gap-2">
              <button onClick={onCancelUpload} className="px-3 py-1.5 text-xs border border-[#E5E7EB] rounded-lg hover:bg-[#FAFAFA]">Cancel</button>
              {can('Budget.Edit') && (
                <button onClick={onConfirmUpload} disabled={uploading} className="px-3 py-1.5 text-xs bg-[#1A7A4A] text-white rounded-lg hover:bg-[#1A7A4A]/90 disabled:opacity-60 flex items-center gap-1.5">
                  {uploading ? (<><svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="60" strokeDashoffset="20"/></svg> Uploading...</>) : <>✓ Confirm Upload</>}
                </button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto max-h-64 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#0F1017] text-white sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Item</th>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2 text-left">Unit</th>
                  <th className="px-3 py-2 text-right">Rate</th>
                  <th className="px-3 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F4F6]">
                {uploadPreview.slice(0,30).map((r,i) => (
                  <tr key={i} className={
                    r.row_type==='package'    ? 'bg-[#2E2C29]' :
                    r.row_type==='activity'   ? 'bg-[#E8EDF2]' :
                    r.row_type==='material'   ? 'bg-blue-50' :
                    r.row_type==='labour'     ? 'bg-green-50' :
                    r.row_type==='subcontract'? 'bg-orange-50' :
                    r.row_type==='equipment'  ? 'bg-amber-50' : ''
                  }>
                    <td className={`px-3 py-1.5 font-mono text-xs ${r.row_type==='package'?'text-white':r.row_type==='activity'?'font-bold text-[#0F1017]':'text-[#6B7280]'}`}>{r.item_number||''}</td>
                    <td className={`px-3 py-1.5 ${r.row_type==='package'?'font-bold text-white':r.row_type==='activity'?'font-semibold text-[#0F1017]':'text-[#6B7280]'}`}>{r.description}</td>
                    <td className="px-3 py-1.5 text-[#9CA3AF]">{r.row_type}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{r.qty ? fmtN(r.qty) : '—'}</td>
                    <td className="px-3 py-1.5">{r.unit||'—'}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{r.unit_rate ? fmtN(r.unit_rate) : '—'}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-bold">{fmtN(r.total_budget)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Toolbar */}
      {items.length > 0 && !uploadPreview && (
        <div className="flex items-center gap-2">
          {selected.size > 0 && can('Budget.Edit') && (
            <button onClick={onDeleteSelected} className="px-3 py-1.5 text-xs bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100">
              🗑 Delete {selected.size}
            </button>
          )}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-16 text-[#9CA3AF] text-sm">Loading...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-[#9CA3AF] text-sm">لا يوجد بيانات — ارفع Excel أولاً</div>
      ) : (
        <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#0F1017] text-white sticky top-0">
                <tr>
                  <th className="w-8 px-3 py-3"></th>
                  <th className="text-left px-3 py-3 font-semibold uppercase tracking-wide w-20">Item No.</th>
                  <th className="text-left px-3 py-3 font-semibold uppercase tracking-wide">Description</th>
                  <th className="text-left px-3 py-3 font-semibold uppercase tracking-wide w-28">Cost Code</th>
                  <th className="text-left px-3 py-3 font-semibold uppercase tracking-wide w-24">Resource</th>
                  <th className="text-left px-3 py-3 font-semibold uppercase tracking-wide w-32">Resource Code</th>
                  <th className="text-left px-3 py-3 font-semibold uppercase tracking-wide">Description for Resource Code</th>
                  <th className="text-left px-3 py-3 font-semibold uppercase tracking-wide w-32">Code</th>
                  <th className="text-right px-3 py-3 font-semibold uppercase tracking-wide w-24">Qty</th>
                  <th className="text-left px-3 py-3 font-semibold uppercase tracking-wide w-16">Unit</th>
                  <th className="text-right px-3 py-3 font-semibold uppercase tracking-wide w-28">Rate (SAR)</th>
                  <th className="text-right px-3 py-3 font-semibold uppercase tracking-wide w-32">Total (SAR)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F3F4F6]">
                {items.map((item, idx) => {
                  const rt = item.row_type
                  if (rt === 'package' || item.is_package_header) {
                    return (
                      <tr key={item.id||idx} className="bg-[#2E2C29]">
                        <td colSpan={2} className="px-3 py-2.5 text-white font-bold text-xs">{item.item_number}</td>
                        <td colSpan={9} className="px-3 py-2.5 text-white font-bold text-xs uppercase tracking-wide">{item.description}</td>
                        <td className="px-3 py-2.5 text-right font-mono font-bold text-amber-300 text-xs">{fmtN(item.total_budget)}</td>
                      </tr>
                    )
                  }
                  if (rt === 'activity') {
                    return (
                      <tr key={item.id||idx} className="bg-[#E8EDF2] font-semibold">
                        <td className="px-3 py-2 text-center"><input type="checkbox" checked={selected.has(item.id)} onChange={()=>onToggle(item.id)}/></td>
                        <td className="px-3 py-2 font-mono text-[#6B7280]">{item.item_number}</td>
                        <td className="px-3 py-2 text-[#0F1017] font-semibold">{item.description}</td>
                        <td className="px-3 py-2 font-mono text-[#065F46]">{item.cost_code}</td>
                        <td className="px-3 py-2 text-[#6B7280] italic text-xs">activity</td>
                        <td className="px-3 py-2"></td>
                        <td className="px-3 py-2 text-[#9CA3AF] italic">—</td>
                        <td className="px-3 py-2"></td>
                        <td className="px-3 py-2 text-right font-mono text-[#6B7280]">{item.qty ? fmtN(item.qty) : '—'}</td>
                        <td className="px-3 py-2 text-[#6B7280]">{item.unit || '—'}</td>
                        <td className="px-3 py-2 text-right font-mono text-[#6B7280]">{item.unit_rate ? fmtN(item.unit_rate) : '—'}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-[#0F1017]">{fmtN(item.total_budget)}</td>
                        <td className="px-3 py-2 text-center">{can('Budget.Edit') && <button onClick={()=>onEdit(item)} className="text-xs text-[#9CA3AF] hover:text-[#1A7A4A] px-1.5 py-0.5 rounded hover:bg-[#F0FDF4]">✏️</button>}</td>
                      </tr>
                    )
                  }
                  // Resource rows
                  const rc = RES_COLORS[rt] || { bg:'bg-gray-50', text:'text-gray-600', label: rt }
                  const resTotal = rt==='material' ? item.material_total : rt==='labour' ? item.labour_total : rt==='subcontract' ? item.subcontractor_total : item.equipment_total
                  const resCode = item.budget_material_code || item.labour_code || item.budget_sc_code || item.budget_equipment_code
                  return (
                    <tr key={item.id||idx} className={rc.bg}>
                      <td className="px-3 py-1.5"></td>
                      <td className="px-3 py-1.5 font-mono text-[#9CA3AF] pl-8">{item.item_number}</td>
                      <td className="px-3 py-1.5"></td>
                      <td className="px-3 py-1.5 font-mono text-[#9CA3AF] text-xs">{item.cost_code}</td>
                      <td className={`px-3 py-1.5 font-semibold ${rc.text}`}>{rc.label}</td>
                      <td className="px-3 py-1.5 font-mono text-xs font-semibold text-[#1B3A6B]">{item.resource_code || '—'}</td>
                      <td className="px-3 py-1.5 text-[#374151] text-xs">{item.resource_description || ''}</td>
                      <td className={`px-3 py-1.5 font-mono text-xs ${rc.text}`}>{resCode || '—'}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-[#9CA3AF]">{item.qty ? fmtN(item.qty) : '—'}</td>
                      <td className="px-3 py-1.5 text-[#9CA3AF]">{item.unit || '—'}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-[#9CA3AF]">{item.unit_rate ? fmtN(item.unit_rate) : '—'}</td>
                      <td className={`px-3 py-1.5 text-right font-mono font-bold ${rc.text}`}>{fmtN(resTotal)}</td>
                      <td className="px-3 py-1.5 text-center">{can('Budget.Edit') && <button onClick={()=>onEdit(item)} className="text-xs text-[#9CA3AF] hover:text-[#1A7A4A] px-1.5 py-0.5 rounded hover:bg-[#F0FDF4]">✏️</button>}</td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot className="bg-[#0F1017]">
                <tr>
                  <td colSpan={5} className="px-3 py-3 text-xs font-bold text-slate-400 uppercase">Grand Total</td>
                  <td className="px-3 py-3 text-right text-xs font-bold text-blue-400 font-mono">{fmtN(totalMat)} Mat</td>
                  <td className="px-3 py-3 text-right text-xs font-bold text-green-400 font-mono" colSpan={1}>{fmtN(totalLab)} Lab</td>
                  <td className="px-3 py-3 text-right text-xs font-bold text-orange-400 font-mono">{fmtN(totalSub)} SC</td>
                  <td className="px-3 py-3 text-right text-xs font-bold text-amber-400 font-mono">{fmtN(totalEqp)} Eqp</td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-white font-mono">{fmtN(totalBudget)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-[#0F1017]">Edit — {editItem.row_type}</h3>
              <button onClick={onEditCancel} className="text-[#9CA3AF] hover:text-[#6B7280] text-xl">×</button>
            </div>
            <div className="space-y-3">
              {editItem.row_type === 'activity' && [
                { key:'item_number', label:'Item No.' },
                { key:'description', label:'Description' },
                { key:'cost_code', label:'Cost Code' },
                { key:'package_name', label:'Package' },
                { key:'qty', label:'Qty', type:'number' },
                { key:'unit', label:'Unit' },
                { key:'unit_rate', label:'Budget Rate', type:'number' },
                { key:'total_budget', label:'Total Budget', type:'number' },
                { key:'material_total', label:'Material Total', type:'number' },
                { key:'labour_total', label:'Labour Total', type:'number' },
                { key:'subcontractor_total', label:'Subcontract Total', type:'number' },
                { key:'equipment_total', label:'Equipment Total', type:'number' },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-xs text-[#6B7280] mb-1 block">{f.label}</label>
                  <input type={f.type||'text'} value={editForm[f.key]||''} onChange={e=>onEditChange(f.key,e.target.value)}
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]/30"/>
                </div>
              ))}
              {['material','labour','subcontract','equipment'].includes(editItem.row_type) && [
                { key:'resource_description', label:'Description (e.g. pumps, operators)' },
                { key:'qty', label:'Qty', type:'number' },
                { key:'unit', label:'Unit' },
                { key:'unit_rate', label:'Rate', type:'number' },
                ...(editItem.row_type==='material'    ? [{ key:'material_total',      label:'Total', type:'number' },{ key:'budget_material_code', label:'Sub Code', isCode:true }] : []),
                ...(editItem.row_type==='labour'      ? [{ key:'labour_total',        label:'Total', type:'number' },{ key:'labour_code',          label:'Sub Code', isCode:true }] : []),
                ...(editItem.row_type==='subcontract' ? [{ key:'subcontractor_total', label:'Total', type:'number' },{ key:'budget_sc_code',        label:'Sub Code', isCode:true }] : []),
                ...(editItem.row_type==='equipment'   ? [{ key:'equipment_total',     label:'Total', type:'number' },{ key:'budget_equipment_code', label:'Sub Code', isCode:true }] : []),
              ].map(f => (
                <div key={f.key}>
                  <label className="text-xs text-[#6B7280] mb-1 block">{f.label}</label>
                  {f.isCode ? (
                    <>
                      <input list={`sc-list-${f.key}`} type="text" value={editForm[f.key]||''} onChange={e=>onEditChange(f.key,e.target.value)}
                        className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]/30" placeholder="اختر أو اكتب sub code"/>
                      <datalist id={`sc-list-${f.key}`}>
                        {subCodes.map(sc => <option key={sc.id} value={sc.code}>{sc.code} — {sc.description}</option>)}
                      </datalist>
                    </>
                  ) : (
                    <input type={f.type||'text'} value={editForm[f.key]||''} onChange={e=>onEditChange(f.key,e.target.value)}
                      className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]/30"/>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={onEditCancel} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-lg hover:bg-[#FAFAFA]">Cancel</button>
              {can('Budget.Edit') && (
                <button onClick={onEditSave} className="px-4 py-2 text-sm bg-[#1A7A4A] text-white rounded-lg hover:bg-[#15693f]">Save</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default function Budget({ initProject }) {
  const [projects, setProjects] = useState([])
  const [selectedProject, setSelectedProject] = useState(initProject || null)
  useEffect(() => { if (initProject) setSelectedProject(initProject) }, [initProject?.id])
  const { can } = useProjectAuth(selectedProject?.id)
  useEffect(() => {
    if (selectedProject?.id) {
      loadItems(selectedProject.id)
      if (selectedProject.enable_boq_lines) loadBOQItems(selectedProject.id)
    }
  }, [selectedProject?.id])
  const [items, setItems] = useState([])
  const [boqItems, setBoqItems] = useState([]) // للربط بالـ BOQ
  const [costCodes, setCostCodes] = useState([]) // map code -> id
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [packageFilter, setPackageFilter] = useState('all')
  const [uploadPreview, setUploadPreview] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [toast, setToast] = useState(null)
  const [ccEditId, setCcEditId] = useState(null)
  const [ccEditVal, setCcEditVal] = useState('')
  const [scEditId, setScEditId] = useState(null)
  const [scEditVal, setScEditVal] = useState('')
  const [nrmEditId, setNrmEditId] = useState(null)
  const [nrmEditVal, setNrmEditVal] = useState('')
  const [nrmElements, setNrmElements] = useState([])
  const [selected, setSelected] = useState(new Set())
  const [activeTab, setActiveTab] = useState('direct')
  const [tenderItems, setTenderItems] = useState([])
  const [tenderLoading, setTenderLoading] = useState(false)
  const [tenderUploadPreview, setTenderUploadPreview] = useState(null)
  const [tenderUploading, setTenderUploading] = useState(false)
  const [tenderUploadMode, setTenderUploadMode] = useState(null) // 'replace' | 'append' | null = asking
  const [tenderSelected, setTenderSelected] = useState(new Set())
  const [tenderEditItem, setTenderEditItem] = useState(null)
  const [tenderEditForm, setTenderEditForm] = useState({})
  const [tenderViewMode, setTenderViewMode] = useState('table')
  const [analysisCodes, setAnalysisCodes] = useState([])
  const [directEv, setDirectEv] = useState({}) // cost_code -> {ev_method, ev_progress, ev_qty, ev_rate}
  const [directEvModal, setDirectEvModal] = useState(null) // row being edited
  const [directEvLocal, setDirectEvLocal] = useState({})
  const [showAddModal, setShowAddModal] = useState(false)
  const [editItem, setEditItem] = useState(null)
  const [addForm, setAddForm] = useState({ package_name:'', cost_code:'', sub_code:'', description:'', total_budget:'', row_type:'', included: false })
  const [evLocal, setEvLocal] = useState({})
  const [evSaving, setEvSaving] = useState({})
  const [evModal, setEvModal] = useState(null)
  const [showTenderAddModal, setShowTenderAddModal] = useState(false)
  const [tenderAddForm, setTenderAddForm] = useState({
    item_number:'', description:'', cost_code:'', package_name:'',
    qty:'', unit:'', unit_rate:'',
    mat_code:'', mat_desc:'', mat_qty:'', mat_unit:'', mat_rate:'', mat_total:'',
    lab_code:'', lab_desc:'', lab_qty:'', lab_unit:'', lab_rate:'', lab_total:'',
    sc_code:'',  sc_desc:'',  sc_qty:'',  sc_unit:'',  sc_rate:'',  sc_total:'',
    eqp_code:'', eqp_desc:'', eqp_qty:'', eqp_unit:'', eqp_rate:'', eqp_total:'',
  })

  async function handleSaveTenderActivity() {
    if (!tenderAddForm.description) return showToast('Description مطلوب', 'error')
    if (!selectedProject) return showToast('اختر مشروع', 'error')
    const maxSort = tenderItems.length ? Math.max(...tenderItems.map(i => i.sort_order||0)) : 0
    let sort = maxSort + 1
    const actTotal = ['mat','lab','sc','eqp'].reduce((s,k) => s + (parseFloat(tenderAddForm[`${k}_total`])||0), 0)
    const actQty   = parseFloat(tenderAddForm.qty) || null
    const actRate  = actQty ? round2(actTotal / actQty) : null
    const rows = [
      // Activity row
      {
        project_id: selectedProject.id, row_type: 'activity', is_package_header: false,
        item_number: tenderAddForm.item_number || null, description: tenderAddForm.description,
        cost_code: tenderAddForm.cost_code || null, package_name: tenderAddForm.package_name || null,
        qty: actQty, unit: tenderAddForm.unit || null, unit_rate: actRate,
        total_budget: actTotal, material_total: parseFloat(tenderAddForm.mat_total)||0,
        labour_total: parseFloat(tenderAddForm.lab_total)||0,
        subcontractor_total: parseFloat(tenderAddForm.sc_total)||0,
        equipment_total: parseFloat(tenderAddForm.eqp_total)||0,
        sort_order: sort++,
      },
      // Material
      { project_id: selectedProject.id, row_type: 'material', is_package_header: false,
        description: 'Material', resource_description: tenderAddForm.mat_desc || null,
        item_number: tenderAddForm.item_number || null,
        cost_code: tenderAddForm.cost_code || null, package_name: tenderAddForm.package_name || null,
        budget_material_code: tenderAddForm.mat_code || null,
        qty: parseFloat(tenderAddForm.mat_qty)||null, unit: tenderAddForm.mat_unit||null,
        unit_rate: parseFloat(tenderAddForm.mat_rate)||null,
        material_total: parseFloat(tenderAddForm.mat_total)||0,
        total_budget:0, labour_total:0, subcontractor_total:0, equipment_total:0, sort_order: sort++ },
      // Labour
      { project_id: selectedProject.id, row_type: 'labour', is_package_header: false,
        description: 'Labour', resource_description: tenderAddForm.lab_desc || null,
        item_number: tenderAddForm.item_number || null,
        cost_code: tenderAddForm.cost_code || null, package_name: tenderAddForm.package_name || null,
        labour_code: tenderAddForm.lab_code || null,
        qty: parseFloat(tenderAddForm.lab_qty)||null, unit: tenderAddForm.lab_unit||null,
        unit_rate: parseFloat(tenderAddForm.lab_rate)||null,
        labour_total: parseFloat(tenderAddForm.lab_total)||0,
        total_budget:0, material_total:0, subcontractor_total:0, equipment_total:0, sort_order: sort++ },
      // Subcontract
      { project_id: selectedProject.id, row_type: 'subcontract', is_package_header: false,
        description: 'Subcontract', resource_description: tenderAddForm.sc_desc || null,
        item_number: tenderAddForm.item_number || null,
        cost_code: tenderAddForm.cost_code || null, package_name: tenderAddForm.package_name || null,
        budget_sc_code: tenderAddForm.sc_code || null,
        qty: parseFloat(tenderAddForm.sc_qty)||null, unit: tenderAddForm.sc_unit||null,
        unit_rate: parseFloat(tenderAddForm.sc_rate)||null,
        subcontractor_total: parseFloat(tenderAddForm.sc_total)||0,
        total_budget:0, material_total:0, labour_total:0, equipment_total:0, sort_order: sort++ },
      // Equipment
      { project_id: selectedProject.id, row_type: 'equipment', is_package_header: false,
        description: 'Equipment', resource_description: tenderAddForm.eqp_desc || null,
        item_number: tenderAddForm.item_number || null,
        cost_code: tenderAddForm.cost_code || null, package_name: tenderAddForm.package_name || null,
        budget_equipment_code: tenderAddForm.eqp_code || null,
        qty: parseFloat(tenderAddForm.eqp_qty)||null, unit: tenderAddForm.eqp_unit||null,
        unit_rate: parseFloat(tenderAddForm.eqp_rate)||null,
        equipment_total: parseFloat(tenderAddForm.eqp_total)||0,
        total_budget:0, material_total:0, labour_total:0, subcontractor_total:0, sort_order: sort++ },
    ]
    const { error } = await supabase.from('tender_direct_items').insert(rows)
    if (error) return showToast('فشل: ' + error.message, 'error')
    showToast('تم الإضافة')
    setShowTenderAddModal(false)
    setTenderAddForm({ item_number:'', description:'', cost_code:'', package_name:'', qty:'', unit:'', unit_rate:'', mat_code:'', mat_qty:'', mat_unit:'', mat_rate:'', mat_total:'', lab_code:'', lab_qty:'', lab_unit:'', lab_rate:'', lab_total:'', sc_code:'', sc_qty:'', sc_unit:'', sc_rate:'', sc_total:'', eqp_code:'', eqp_qty:'', eqp_unit:'', eqp_rate:'', eqp_total:'' })
    loadTenderItems(selectedProject.id)
  }

  const round2 = (n) => Math.round(n * 100) / 100

  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  // Load projects
  useEffect(() => {
    const _pc = getCached('projects_full')
    if (_pc) setProjects(_pc)
    else supabase.from('projects')
      .select('id, name, name_ar, currency, enable_boq_lines, contract_value, organizations(name)')
      .order('name')
      .then(({ data }) => { setProjects(data || []); if(data) setCached('projects_full', data) })
  }, [])

  // Load cost codes map
  useEffect(() => {
    supabase.from('cost_codes').select('id, code, description, level, parent_id').eq('project_id', selectedProject?.id).eq('is_active', true).order('code').then(({ data }) => {
      if (data) { setCostCodes(data); setCached('cost_codes_full', data) }
    })
    supabase.from('nrm1_elements').select('*').order('code').then(({ data }) => {
      if (data) setNrmElements(data)
    })
    supabase.from('analysis_codes').select('*').order('code').then(({ data }) => {
      if (data) setAnalysisCodes(data)
    })
  }, [])

  const loadItems = useCallback(async (projectId) => {
    setLoading(true)
    const { data, error } = await supabase
      .from('budget_summary')
      .select('*, cost_codes(code, description)')
      .eq('project_id', projectId)
      .order('sort_order', { ascending: true })
    if (error) showToast('فشل التحميل: ' + error.message, 'error')
    else setItems(data || [])
    setLoading(false)
  }, [])

  const loadBOQItems = useCallback(async (projectId) => {
    const { data } = await supabase
      .from('boq_items')
      .select('item_no, description')
      .eq('project_id', projectId)
      .order('item_no')
    setBoqItems(data || [])
  }, [])

  const handleProjectSelect = (p) => {
    setSelectedProject(p)
    setItems([])
    setUploadPreview(null)
    setSelected(new Set())
    setPackageFilter('all')
    if (p) {
      loadItems(p.id)
      if (p.enable_boq_lines) loadBOQItems(p.id)
    }
  }

  const packagesData = (() => {
    const result = []; let last = null
    ;[...items].sort((a,b) => (a.sort_order||0)-(b.sort_order||0)).forEach(item => {
      const pkg    = (item.package_name||'').trim()
      const pkgLow = pkg.toLowerCase()
      const rt     = (item.row_type||'').toLowerCase()
      const isTotal= rt === 'total' || pkgLow.startsWith('total ') || pkgLow === 'total'
      if (pkgLow.includes('grand total')) { last = null; return }
      if (!isTotal && pkg && pkgLow !== 'nan') {
        if (!last || last.name !== pkg) { last = {name:pkg, items:[], totalRowVal:0}; result.push(last) }
        last.items.push(item)
      } else if (isTotal) {
        const val = parseFloat(item.total_budget)||0
        if (last) { last.totalRowVal = val; last = null }
        else {
          const key = pkgLow.replace('total ','').trim()
          const m = result.find(p => p.name.toLowerCase() === key)
          if (m && val > 0) m.totalRowVal = val
        }
      }
    })
    result.forEach(p => {
      const s = p.items.reduce((a,i) => a+(parseFloat(i.total_budget)||0), 0)
      p.total = s > 0 ? s : p.totalRowVal
    })
    return result.filter(p => !p.name.toLowerCase().includes('grand total')).sort((a,b) => b.total-a.total)
  })()
  const totalBudget = packagesData.reduce((s,p) => s+p.total, 0)
  const totalEV = (() => {
    // Build same package structure as packagesData but for EV
    const evPkgs = {}; let lastPkg = null
    ;[...items].sort((a,b) => (a.sort_order||0)-(b.sort_order||0)).forEach(item => {
      const pkg    = (item.package_name||'').trim()
      const pkgLow = pkg.toLowerCase()
      const rt     = (item.row_type||'').toLowerCase()
      const isTotal= rt === 'total' || pkgLow.startsWith('total ') || pkgLow === 'total'
      if (pkgLow.includes('grand total')) { lastPkg = null; return }
      if (!isTotal && pkg && pkgLow !== 'nan') {
        if (!evPkgs[pkg]) evPkgs[pkg] = { detailEV: 0, totalRowEV: null }
        const ev = (() => {
          const method   = evLocal[item.id]?.ev_method   !== undefined ? evLocal[item.id].ev_method   : (item.ev_method ?? 'progress')
          const progress = evLocal[item.id]?.ev_progress !== undefined ? evLocal[item.id].ev_progress : item.ev_progress
          const qty      = evLocal[item.id]?.ev_qty      !== undefined ? evLocal[item.id].ev_qty      : item.ev_qty
          const rate     = evLocal[item.id]?.ev_rate     !== undefined ? evLocal[item.id].ev_rate     : item.ev_rate
          const budget   = parseFloat(item.total_budget) || 0
          if (method === 'progress' && progress != null && progress !== '') return budget * (parseFloat(progress) / 100)
          if (method === 'qty_rate' && qty != null && rate != null && qty !== '' && rate !== '') return parseFloat(qty) * parseFloat(rate)
          return parseFloat(item.ev_amount) || 0
        })()
        evPkgs[pkg].detailEV += ev
        lastPkg = pkg
      } else if (isTotal) {
        const evVal = parseFloat(item.ev_amount) || 0
        const key = lastPkg || pkgLow.replace(/^total\s+/,'').trim()
        const match = evPkgs[key] || Object.entries(evPkgs).find(([k]) => k.toLowerCase() === key)?.[1]
        if (match && evVal > 0) match.totalRowEV = evVal
        lastPkg = null
      }
    })
    return Object.values(evPkgs).reduce((s, p) => s + (p.totalRowEV != null ? p.totalRowEV : p.detailEV), 0)
  })()
  const evPct = totalBudget > 0 ? (totalEV / totalBudget) * 100 : 0
  const uniquePackages = [...new Set(items.map(i => i.package_name).filter(Boolean))]

  const filtered = items.filter(i => {
    const matchSearch = !search ||
      i.description?.toLowerCase().includes(search.toLowerCase()) ||
      i.boq_item_no?.toLowerCase().includes(search.toLowerCase()) ||
      i.package_name?.toLowerCase().includes(search.toLowerCase()) ||
      i.cost_codes?.code?.toLowerCase().includes(search.toLowerCase())
    const isTotalRow = i.row_type === 'total' || i.row_type === 'Total'
    if (isTotalRow && packageFilter !== 'all') {
      const pkgItems = items.filter(x => x.package_name === packageFilter && x.row_type !== 'total' && x.row_type !== 'Total')
      const ccIds = new Set(pkgItems.map(x => x.cost_code_id).filter(Boolean))
      return ccIds.has(i.cost_code_id)
    }
    const matchPkg = packageFilter === 'all' || i.package_name === packageFilter || isTotalRow
    return matchSearch && matchPkg
  })

  // Parse Excel
  const parseExcel = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' })
        const ws = wb.Sheets[wb.SheetNames[0]]

        // Find header row — look for row where col A is exactly 'package' or 'package ★'
        const range = XLSX.utils.decode_range(ws['!ref'] || 'A1:A1')
        let headerRow = 0
        for (let r = 0; r <= Math.min(6, range.e.r); r++) {
          const cell = ws[XLSX.utils.encode_cell({ r, c: 0 })]
          if (cell) {
            const v = String(cell.v).toLowerCase().trim()
            // Must be short (not the instruction row) and contain 'package'
            if (v.includes('package') && v.length < 30) { headerRow = r; break }
          }
        }
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '', range: headerRow })

        const costCodesMap = {}
        costCodes.forEach(c => { costCodesMap[c.code.trim().toLowerCase()] = c.id })
        const boqNos = new Set(boqItems.map(b => String(b.item_no).trim().toLowerCase()))

        // Normalize key: remove special chars
        const cleanKey = (k) => k.toLowerCase().replace(/[★\s\/\.\-]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '')

        const normalized = rows.map(row => {
          // Build normalized key map
          const l = {}
          Object.keys(row).forEach(k => { l[cleanKey(k)] = row[k] })

          // Flexible field getter
          const get = (...keys) => {
            for (const k of keys) {
              const v = l[cleanKey(k)]
              if (v !== undefined && v !== '') return v
            }
            return ''
          }

          const pkg = (() => { const v=String(get('package ★','package','package_name')).trim(); return (v&&v!=='nan'&&!v.startsWith('←'))?v:'' })()
          const rt  = String(get('row_type')).trim().toLowerCase()
          const pl  = pkg.toLowerCase()
          const isTotal = rt==='total' || pl.startsWith('total ') || pl==='total'
          const bRaw= String(get('total_budget ★','total_budget','budget')).trim()
          const isIncluded = bRaw.toLowerCase()==='included'
          const bVal= isIncluded ? 0 : (parseFloat(bRaw.replace(/,/g,''))||0)
          if (!pkg && !isTotal) return null
          if (!isTotal && !isIncluded && bVal===0) return null
          const desc = (() => { const v=String(get('description ★sub code or cost code','description','desc')).trim(); return (v&&v!=='nan')?v:pkg })()
          const cc  = String(get('cost_code','costcode')).trim()
          const sc  = String(get('sub_code','subcode')).trim()||null
          const boqNo = String(get('boq_item_no','item_no')).trim()
          const evMethod   = String(get('ev_method')).trim().toLowerCase()||'progress'
          const evProgress = parseFloat(String(get('ev_progress')).replace(/,/g,''))||null
          const evQty      = parseFloat(String(get('ev_qty')).replace(/,/g,''))||null
          const evRate     = parseFloat(String(get('ev_rate')).replace(/,/g,''))||null
          let evAmount = null
          if (evMethod==='progress' && evProgress!=null) evAmount = bVal*(evProgress/100)
          if (evMethod==='qty_rate' && evQty!=null && evRate!=null) evAmount = evQty*evRate
          return {
            project_id:selectedProject.id, cost_code_id:cc?(costCodesMap[cc.toLowerCase()]||null):null,
            boq_item_no:boqNo||null, description:desc, package_name:pkg||null, sub_code:sc,
            qty:0, unit:null, labor_rate:0, labor_total:0, material_rate:0, material_total:0,
            subcontract_rate:0, subcontract_total:0, equipment_rate:0, equipment_total:0,
            total_budget:bVal, notes:isIncluded?'included':null,
            ev_method:evMethod||null, ev_progress:evProgress, ev_qty:evQty, ev_rate:evRate, ev_amount:evAmount,
            _cost_code_text:cc.toLowerCase()||null, _cost_code_found:cc?!!costCodesMap[cc.toLowerCase()]:null,
            _boq_linked:selectedProject?.enable_boq_lines&&boqNo&&boqNos.has(boqNo.toLowerCase()),
            _is_included:isIncluded, _is_total_row:isTotal,
          }
        }).filter(Boolean)

        resolve(normalized)
      } catch (err) { reject(err) }
    }
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })

  const handleFileDrop = async (file) => {
    if (!file) return
    if (!selectedProject) return showToast('اختر مشروع أولاً', 'error')
    const ext = file.name.split('.').pop().toLowerCase()
    if (!['xlsx', 'xls', 'csv'].includes(ext)) return showToast('Excel أو CSV فقط', 'error')
    try {
      const rows = await parseExcel(file)
      if (!rows.length) return showToast('لا يوجد صفوف صالحة', 'error')
      setUploadPreview(rows)
    } catch (e) { showToast('خطأ: ' + e.message, 'error') }
  }

  const handleUploadConfirm = async () => {
    setUploading(true)

    // لا نحذف البيانات القديمة — نضيف فقط (append)

    // بناء الـ insert مع الحفاظ على row_type من _is_total_row
    const cleanInsert = uploadPreview.map((row, idx) => ({
      project_id:   row.project_id,
      package_name: row.package_name,
      cost_code_id: row.cost_code_id,
      sub_code:     row.sub_code,
      description:  row.description,
      total_budget: row.total_budget,
      notes:        row.notes,
      row_type:     row._is_total_row ? 'total' : null,
      sort_order:   idx,
      ev_method:    row.ev_method || null,
      ev_progress:  row.ev_progress || null,
      ev_qty:       row.ev_qty || null,
      ev_rate:      row.ev_rate || null,
      ev_amount:    row.ev_amount || null,
    }))

    const { error } = await supabase.from('budget_summary').insert(cleanInsert)
    setUploading(false)
    if (error) showToast('فشل الرفع: ' + error.message, 'error')
    else {
      showToast(`تم استيراد ${uploadPreview.length} بند بنجاح`)
      setUploadPreview(null)
      loadItems(selectedProject.id)
    }
  }

  const handleDeleteSelected = async () => {
    if (!selected.size) return
    if (!window.confirm(`حذف ${selected.size} بند؟`)) return
    const { error } = await supabase.from('budget_summary').delete().in('id', [...selected])
    if (error) showToast('فشل الحذف: ' + error.message, 'error')
    else { showToast('تم الحذف'); setSelected(new Set()); loadItems(selectedProject.id) }
  }

  const downloadTemplate = () => {
    const headers = ['package ★', 'cost_code ★', 'sub_code', 'description ★sub code or cost code', 'total_budget ★', 'row_type']
    const rows = [
      // Sub code rows with included budget
      { 'package ★': 'Temporary dewatering',        'cost_code ★': '31 23 19', sub_code: 'S-C.Dew.1',   'description ★sub code or cost code': 'Action-subcontractor',          'total_budget ★': 'included', row_type: '' },
      { 'package ★': 'Temporary dewatering',        'cost_code ★': '31 23 19', sub_code: 'S-C.Dew.2',   'description ★sub code or cost code': 'Action-subcontractor-Extension', 'total_budget ★': 'included', row_type: '' },
      { 'package ★': 'Temporary dewatering',        'cost_code ★': '31 23 19', sub_code: 'Petty Cash',  'description ★sub code or cost code': 'Petty Cash-Dewatering',          'total_budget ★': 'included', row_type: '' },
      // Total row (row_type = total) — green in display
      { 'package ★': 'Total Temporary dewatering',  'cost_code ★': '31 23 19', sub_code: '',             'description ★sub code or cost code': '',                              'total_budget ★': 6835080,    row_type: 'total' },
      // Sub code rows with actual budget
      { 'package ★': 'Bored concrete piling',       'cost_code ★': '31 63 00', sub_code: 'S-C.BORED.1', 'description ★sub code or cost code': 'SAUDI BAUER - Bored Piling',     'total_budget ★': 28143094,   row_type: '' },
      { 'package ★': 'Bored concrete piling',       'cost_code ★': '31 63 00', sub_code: 'Mat-BORED1',  'description ★sub code or cost code': 'Testing & Pile Load Test',       'total_budget ★': 'included', row_type: '' },
      { 'package ★': 'Total Bored concrete piling', 'cost_code ★': '31 63 00', sub_code: '',             'description ★sub code or cost code': '',                              'total_budget ★': 31507508,   row_type: 'total' },
      // Bulk row — no sub codes
      { 'package ★': 'External envelope',           'cost_code ★': '07 25 00', sub_code: '',             'description ★sub code or cost code': 'External thermal protection',    'total_budget ★': 4598000,    row_type: '' },
      { 'package ★': 'Total External envelope',     'cost_code ★': '07 25 00', sub_code: '',             'description ★sub code or cost code': '',                              'total_budget ★': 4598000,    row_type: 'total' },
    ]
    const ws = XLSX.utils.json_to_sheet(rows, { header: headers })
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Direct Budget')
    XLSX.writeFile(wb, 'direct_budget_template.xlsx')
  }

  const exportData = () => {
    if (!items.length) return showToast('مفيش بيانات للتصدير', 'error')
    const rows = items.map(i => ({
      'package ★':                          i.package_name || '',
      'cost_code':                          i.cost_codes?.code || '',
      'sub_code':                           i.sub_code || '',
      'description ★sub code or cost code': i.description || '',
      'total_budget ★':                     i.notes === 'included' ? 'included' : (i.total_budget || 0),
      'row_type':                           i.row_type || '',
      'ev_method':                          i.ev_method || '',
      'ev_progress':                        i.ev_progress ?? '',
      'ev_qty':                             i.ev_qty ?? '',
      'ev_rate':                            i.ev_rate ?? '',
    }))
    const headers = ['package ★','cost_code','sub_code','description ★sub code or cost code','total_budget ★','row_type','ev_method','ev_progress','ev_qty','ev_rate']
    const ws = XLSX.utils.json_to_sheet(rows, { header: headers })
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Direct Budget')
    XLSX.writeFile(wb, `direct_budget_${selectedProject?.name || 'export'}.xlsx`)
  }

  async function handleSaveItem() {
    if (!addForm.description) return showToast('Description مطلوب', 'error')
    // resolve cost_code_id
    let cost_code_id = null
    if (addForm.cost_code) {
      const cc = costCodes.find(c => c.code.trim() === addForm.cost_code.trim())
      if (cc) cost_code_id = cc.id
    }
    const maxSort = items.length ? Math.max(...items.map(i => i.sort_order || 0)) : 0
    let newSortOrder = maxSort + 1
    if (!editItem && addForm.package_name) {
      const pkgItems = items.filter(i => i.package_name === addForm.package_name)
      const totalRow = pkgItems.find(i => (i.row_type||'').toLowerCase() === 'total')
      const lastNonTotal = pkgItems.filter(i => (i.row_type||'').toLowerCase() !== 'total').slice(-1)[0]
      if (totalRow) {
        newSortOrder = totalRow.sort_order
        const toShift = items.filter(i => (i.sort_order||0) >= totalRow.sort_order)
        for (const r of toShift) {
          await supabase.from('budget_summary').update({ sort_order: (r.sort_order||0) + 1 }).eq('id', r.id)
        }
      } else if (lastNonTotal) {
        newSortOrder = (lastNonTotal.sort_order || 0) + 1
      }
    }
    const payload = {
      project_id:   selectedProject.id,
      package_name: addForm.package_name || null,
      cost_code_id,
      sub_code:     addForm.sub_code || null,
      description:  addForm.description,
      total_budget: addForm.included ? 0 : (parseFloat(addForm.total_budget) || 0),
      notes:        addForm.included ? 'included' : null,
      row_type:     addForm.row_type || null,
      sort_order:   editItem ? editItem.sort_order : newSortOrder,
    }
    let error
    if (editItem) {
      ({ error } = await supabase.from('budget_summary').update(payload).eq('id', editItem.id))
    } else {
      ({ error } = await supabase.from('budget_summary').insert(payload))
    }
    if (error) return showToast('فشل الحفظ: ' + error.message, 'error')
    showToast(editItem ? 'تم التعديل' : 'تم الإضافة')
    setShowAddModal(false)
    setEditItem(null)
    setAddForm({ package_name:'', cost_code:'', sub_code:'', description:'', total_budget:'', row_type:'', included: false })
    loadItems(selectedProject.id)
  }

    const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set())
    else setSelected(new Set(filtered.map(i => i.id)))
  }

  // Direct Budget — restructured: group by Resource Code (Material/Labour/Subcontract/Equipment
  // row's resource_code), then roll those up under their parent Cost Code. The Cost Code no longer
  // carries its own budget directly — its total is purely the sum of its Resource Codes' budgets.
  const directResourceRows = useMemo(() => {
    const resourceRows = tenderItems.filter(i => ['material','labour','subcontract','equipment'].includes(i.row_type))
    const grouped = {}
    for (const item of resourceRows) {
      const rcode = item.resource_code || `__unassigned__${item.id}`
      const resTotal = item.row_type === 'material' ? item.material_total
        : item.row_type === 'labour' ? item.labour_total
        : item.row_type === 'subcontract' ? item.subcontractor_total
        : item.equipment_total
      if (!grouped[rcode]) {
        grouped[rcode] = {
          resource_code: item.resource_code || null,
          resource_description: item.resource_description || '',
          resource_type: item.row_type,
          cost_code: item.cost_code,
          package_name: item.package_name,
          total_budget: 0,
          min_sort: item.sort_order ?? 9999,
          items: [],
        }
      }
      grouped[rcode].items.push(item)
      grouped[rcode].total_budget += parseFloat(resTotal) || 0
      if ((item.sort_order ?? 9999) < grouped[rcode].min_sort) grouped[rcode].min_sort = item.sort_order ?? 9999
    }
    return Object.values(grouped).sort((a,b) => a.min_sort - b.min_sort)
  }, [tenderItems])

  // Cost Code rollup — purely a sum of the Resource Codes underneath it. No budget or EV
  // is entered directly on the Cost Code anymore; it just reflects what's below it.
  const directBudgetRows = useMemo(() => {
    const grouped = {}
    for (const row of directResourceRows) {
      const cc = row.cost_code || '__none__'
      if (!grouped[cc]) grouped[cc] = { cost_code: row.cost_code, package_name: row.package_name, resources: [], total_budget: 0, min_sort: row.min_sort }
      grouped[cc].resources.push(row)
      grouped[cc].total_budget += row.total_budget
      if (row.min_sort < grouped[cc].min_sort) grouped[cc].min_sort = row.min_sort
    }
    return Object.values(grouped).sort((a,b) => a.min_sort - b.min_sort)
  }, [directResourceRows])

  const directTotalBudget = directBudgetRows.reduce((s,r) => s + (parseFloat(r.total_budget) || 0), 0)

  const calcResourceEv = (row) => {
    const ev = directEv[row.resource_code] || {}
    const method = ev.ev_method || 'progress'
    const budget = parseFloat(row.total_budget) || 0
    if (method === 'progress' && ev.ev_progress != null) return budget * (parseFloat(ev.ev_progress) / 100)
    if (method === 'qty_rate' && ev.ev_qty != null && ev.ev_rate != null) return parseFloat(ev.ev_qty) * parseFloat(ev.ev_rate)
    if (method === 'manual' && ev.ev_manual != null) return parseFloat(ev.ev_manual)
    return 0
  }

  // Cost Code EV is purely a rollup — sum of its Resource Codes' EV
  const calcCostCodeEv = (ccRow) => ccRow.resources.reduce((s, r) => s + calcResourceEv(r), 0)

  const totalDirectEV = directResourceRows.reduce((s, r) => s + calcResourceEv(r), 0)
  const totalDirectEVPct = directTotalBudget > 0 ? totalDirectEV / directTotalBudget * 100 : 0

  async function loadTenderItems(pid) {
    setTenderLoading(true)
    const { data } = await supabase.from('tender_direct_items')
      .select('*').eq('project_id', pid).order('sort_order')
    setTenderItems(data || [])
    setTenderLoading(false)
  }

  async function loadDirectEv(pid) {
    const { data } = await supabase.from('direct_budget_ev').select('*').eq('project_id', pid)
    if (data) {
      const map = {}
      data.forEach(r => { map[r.resource_code] = { ev_method: r.ev_method, ev_progress: r.ev_progress, ev_qty: r.ev_qty, ev_rate: r.ev_rate, ev_manual: r.ev_manual, ev_amount: r.ev_amount } })
      setDirectEv(map)
    }
  }

  async function saveDirectEv(resource_code, updates) {
    const payload = { project_id: selectedProject.id, resource_code, ...updates, updated_at: new Date().toISOString() }
    await supabase.from('direct_budget_ev').upsert(payload, { onConflict: 'project_id,resource_code' })
    setDirectEv(prev => ({ ...prev, [resource_code]: { ...(prev[resource_code] || {}), ...updates } }))
  }

  useEffect(() => {
    if (selectedProject?.id) {
      loadTenderItems(selectedProject.id)
      loadDirectEv(selectedProject.id)
    }
  }, [selectedProject?.id]) // eslint-disable-line

  async function parseTenderExcel(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = e => {
        try {
          const wb = XLSX.read(e.target.result, { type: 'array', cellFormula: false, cellNF: false })
          const ws = wb.Sheets[wb.SheetNames[0]]
          const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' })

          // Find header row (contains Item No. / Description / Resource / Qty)
          let headerIdx = 1
          for (let i = 0; i < Math.min(raw.length, 10); i++) {
            const joined = raw[i].join('|').toLowerCase()
            if (joined.includes('item') && joined.includes('description') && joined.includes('qty')) {
              headerIdx = i; break
            }
          }
          const headers = raw[headerIdx].map(h =>
            String(h).trim().toLowerCase()
              .replace(/[\s\/\(\)]+/g, '_').replace(/_+/g, '_').replace(/_sar_?$/, '').replace(/_$/, '')
          )
          // Actual Excel column layout (v4):
          // A(0)=Item No. | B(1)=Description | C(2)=Resource Description | D(3)=Cost Code
          // E(4)=Resource | F(5)=code (old) | G(6)=Resource Code (new) | H(7)=Qty | I(8)=Unit | J(9)=Rate | K(10)=Total
          const iItem=0, iDesc=1, iResDesc=2, iCC=3, iRes=4, iCode=5, iResourceCode=6, iQty=7, iUnit=8, iRate=9, iTotal=10

          const RESOURCE_TYPES = ['material','labour','subcontract','equipment']

          let sort = 0
          let currentPkg = null
          let currentPkgCC = null
          let currentActivity = null
          const result = []

          for (let ri = headerIdx + 1; ri < raw.length; ri++) {
            const rowArr = raw[ri]
            // Col A contains "  ItemNo   Description" merged — split them
            const colA    = String(rowArr[iItem]    ?? '').trim()
            const colB    = String(rowArr[iDesc]    ?? '').trim()
            const resDesc = String(rowArr[iResDesc] ?? '').trim() || null
            const colC    = String(rowArr[iCC]      ?? '').trim()
            const res     = String(rowArr[iRes]     ?? '').trim().toLowerCase()
            const code    = String(rowArr[iCode]    ?? '').trim()
            const resourceCode = String(rowArr[iResourceCode] ?? '').trim() || null
            const qty     = parseFloat(rowArr[iQty])  || null
            const unit    = String(rowArr[iUnit]    ?? '').trim() || null
            const rate    = parseFloat(rowArr[iRate]) || null
            const total   = parseFloat(rowArr[iTotal])|| 0

            // Extract itemNo and desc from col A (format: "  1.1  DESCRIPTION")
            const matchA = colA.match(/^\s*(\d[\d.]*)\s+(.+)$/)
            const itemNo = matchA ? matchA[1].trim() : colA.trim()
            const descA  = matchA ? matchA[2].trim() : ''
            // col B = description for activity rows, col A has item+desc for package/activity
            const desc   = colB || descA

            if (!colA && !res && !colB) continue
            if (colA.toLowerCase().includes('total tender')) continue
            if (colA.toLowerCase().startsWith('total —')) continue

            if (RESOURCE_TYPES.includes(res)) {
              // ── Resource row ──────────────────────────────────────────────
              result.push({
                project_id: selectedProject.id,
                description: res.charAt(0).toUpperCase() + res.slice(1),
                resource_description: resDesc,
                resource_code: resourceCode,
                package_name: currentPkg,
                cost_code: currentActivity?.cc || currentPkgCC || null,
                item_number: currentActivity?.itemNo || null,
                row_type: res,
                is_package_header: false,
                total_budget: 0,
                material_total:      res === 'material'    ? total : 0,
                subcontractor_total: res === 'subcontract' ? total : 0,
                equipment_total:     res === 'equipment'   ? total : 0,
                labour_total:        res === 'labour'      ? total : 0,
                qty, unit, unit_rate: rate,
                labour_code:          res === 'labour'      ? code || null : null,
                budget_material_code: res === 'material'    ? code || null : null,
                budget_sc_code:       res === 'subcontract' ? code || null : null,
                budget_equipment_code:res === 'equipment'   ? code || null : null,
                sort_order: sort++,
              })

            } else if (res === 'activity') {
              // ── Activity row ──────────────────────────────────────────────
              currentActivity = { itemNo, desc, cc: colC || currentPkgCC }
              result.push({
                project_id: selectedProject.id,
                description: desc,
                package_name: currentPkg,
                cost_code: colC || currentPkgCC || null,
                item_number: itemNo || null,
                row_type: 'activity',
                is_package_header: false,
                total_budget: total,
                material_total: 0, subcontractor_total: 0, equipment_total: 0, labour_total: 0,
                qty, unit, unit_rate: rate,
                sort_order: sort++,
              })

            } else if (itemNo && !res) {
              // ── Package header row ────────────────────────────────────────
              currentPkg = desc
              currentPkgCC = colC || null
              currentActivity = null
              result.push({
                project_id: selectedProject.id,
                description: desc,
                package_name: null,
                cost_code: colC || null,
                item_number: itemNo || null,
                row_type: 'package',
                is_package_header: true,
                total_budget: total || 0,
                material_total: 0, subcontractor_total: 0, equipment_total: 0, labour_total: 0,
                qty: null, unit: null, unit_rate: null,
                sort_order: sort++,
              })
            }
          }

          // Back-fill activity totals from resource rows
          for (let i = 0; i < result.length; i++) {
            if (result[i].row_type === 'activity') {
              let mat=0, lab=0, sc=0, eqp=0
              for (let j = i+1; j < result.length; j++) {
                const rt = result[j].row_type
                if (!['material','labour','subcontract','equipment'].includes(rt)) break
                mat += parseFloat(result[j].material_total)      || 0
                lab += parseFloat(result[j].labour_total)        || 0
                sc  += parseFloat(result[j].subcontractor_total) || 0
                eqp += parseFloat(result[j].equipment_total)     || 0
              }
              result[i].material_total      = mat
              result[i].labour_total        = lab
              result[i].subcontractor_total = sc
              result[i].equipment_total     = eqp
            }
          }

          console.log('[Tender Parser] Total rows:', result.length)
          console.log('[Tender Parser] row_types:', result.slice(0,10).map(r=>({rt:r.row_type,desc:r.description?.slice(0,20),total:r.total_budget,mat:r.material_total})))
          resolve(result)
        } catch(err) { reject(err) }
      }
      reader.onerror = reject
      reader.readAsArrayBuffer(file)
    })
  }

  async function deleteTenderSelected() {
    if (!tenderSelected.size || !window.confirm(`حذف ${tenderSelected.size} بند؟`)) return
    await supabase.from('tender_direct_items').delete().in('id', [...tenderSelected])
    setTenderSelected(new Set())
    loadTenderItems(selectedProject.id)
  }

  async function saveTenderEdit() {
    if (!tenderEditItem) return
    const { error } = await supabase.from('tender_direct_items').update({
      description: tenderEditForm.description,
      cost_code: tenderEditForm.cost_code || null,
      qty: parseFloat(tenderEditForm.qty) || null,
      unit: tenderEditForm.unit || null,
      unit_rate: parseFloat(tenderEditForm.unit_rate) || null,
      total_budget: parseFloat(tenderEditForm.total_budget) || 0,
      material_total: parseFloat(tenderEditForm.material_total) || 0,
      labour_total: parseFloat(tenderEditForm.labour_total) || 0,
      subcontractor_total: parseFloat(tenderEditForm.subcontractor_total) || 0,
      equipment_total: parseFloat(tenderEditForm.equipment_total) || 0,
      budget_material_code: tenderEditForm.budget_material_code || null,
      budget_sc_code: tenderEditForm.budget_sc_code || null,
      budget_equipment_code: tenderEditForm.budget_equipment_code || null,
      labour_code: tenderEditForm.labour_code || null,
      resource_description: tenderEditForm.resource_description || null,
    }).eq('id', tenderEditItem.id)
    if (error) showToast('فشل: ' + error.message, 'error')
    else { showToast('تم التحديث', 'success'); setTenderEditItem(null); loadTenderItems(selectedProject.id) }
  }

  async function confirmTenderUpload(mode) {
    if (!tenderUploadPreview?.length) return
    if (tenderUploading) return
    setTenderUploadMode(null)
    setTenderUploading(true)
    if (mode === 'replace') await supabase.from('tender_direct_items').delete().eq('project_id', selectedProject.id)
    const cleanInsert = tenderUploadPreview.map(r => ({
      project_id:           r.project_id,
      description:          r.description,
      package_name:         r.package_name || null,
      cost_code:            r.cost_code || null,
      item_number:          r.item_number || null,
      row_type:             r.row_type || null,
      is_package_header:    r.is_package_header || false,
      total_budget:         r.total_budget || 0,
      material_total:       r.material_total || 0,
      subcontractor_total:  r.subcontractor_total || 0,
      equipment_total:      r.equipment_total || 0,
      labour_total:         r.labour_total || 0,
      qty:                  r.qty || null,
      unit:                 r.unit || null,
      unit_rate:            r.unit_rate || null,
      budget_material_code: r.budget_material_code || null,
      budget_sc_code:       r.budget_sc_code || null,
      budget_equipment_code:r.budget_equipment_code || null,
      labour_code:          r.labour_code || null,
      resource_description: r.resource_description || null,
      resource_code:        r.resource_code || null,
      sort_order:           r.sort_order,
    }))
    const BATCH = 100
    let error = null
    for (let i = 0; i < cleanInsert.length; i += BATCH) {
      const { error: e } = await supabase.from('tender_direct_items').insert(cleanInsert.slice(i, i + BATCH))
      if (e) { error = e; break }
    }
    setTenderUploading(false)
    if (error) showToast('فشل: ' + error.message, 'error')
    else { showToast(`تم استيراد ${tenderUploadPreview.length} بند`, 'success'); setTenderUploadPreview(null); loadTenderItems(selectedProject.id) }
  }

  async function saveCostCode(itemId) {
    const code = ccEditVal.trim()
    if (!code) { setCcEditId(null); return }
    const cc = costCodes.find(c => c.code.trim().toLowerCase() === code.toLowerCase())
    if (!cc) { showToast('كود مش موجود', 'error'); return }
    const { error } = await supabase
      .from('budget_summary')
      .update({ cost_code_id: cc.id })
      .eq('id', itemId)
    if (error) showToast('فشل: ' + error.message, 'error')
    else { showToast('تم التحديث', 'success'); setCcEditId(null); loadItems(selectedProject.id) }
  }

  async function clearCostCode(itemId) {
    const { error } = await supabase.from('budget_summary').update({ cost_code_id: null }).eq('id', itemId)
    if (error) showToast('فشل: ' + error.message, 'error')
    else { showToast('تم المسح', 'success'); setCcEditId(null); loadItems(selectedProject.id) }
  }

  async function saveSubCode(itemId) {
    const { error } = await supabase.from('budget_summary').update({ sub_code: scEditVal.trim() || null }).eq('id', itemId)
    if (error) showToast('فشل: ' + error.message, 'error')
    else { showToast('تم التحديث', 'success'); setScEditId(null); loadItems(selectedProject.id) }
  }

  async function clearSubCode(itemId) {
    const { error } = await supabase.from('budget_summary').update({ sub_code: null }).eq('id', itemId)
    if (error) showToast('فشل: ' + error.message, 'error')
    else { showToast('تم المسح', 'success'); setScEditId(null); loadItems(selectedProject.id) }
  }

  async function saveNrm(itemId) {
    const { error } = await supabase.from('budget_summary').update({ nrm1_element: nrmEditVal || null }).eq('id', itemId)
    if (error) showToast('فشل: ' + error.message, 'error')
    else { showToast('تم التحديث', 'success'); setNrmEditId(null); loadItems(selectedProject.id) }
  }

  async function clearNrm(itemId) {
    const { error } = await supabase.from('budget_summary').update({ nrm1_element: null }).eq('id', itemId)
    if (error) showToast('فشل: ' + error.message, 'error')
    else { showToast('تم المسح', 'success'); setNrmEditId(null); loadItems(selectedProject.id) }
  }

  async function handleDeleteOne(itemId) {
    if (!window.confirm('حذف هذا البند؟')) return
    const { error } = await supabase.from('budget_summary').delete().eq('id', itemId)
    if (error) showToast('فشل الحذف: ' + error.message, 'error')
    else { showToast('تم الحذف', 'success'); loadItems(selectedProject.id) }
  }

  async function saveEV(item) {
    const local = evLocal[item.id] || {}
    const method   = local.ev_method   !== undefined ? local.ev_method   : (item.ev_method   ?? 'progress')
    const progress = local.ev_progress !== undefined ? local.ev_progress : item.ev_progress
    const qty      = local.ev_qty      !== undefined ? local.ev_qty      : item.ev_qty
    const rate     = local.ev_rate     !== undefined ? local.ev_rate     : item.ev_rate
    const manual   = local.ev_manual   !== undefined ? local.ev_manual   : item.ev_manual
    const budget   = parseFloat(item.total_budget) || 0
    let ev_amount  = null
    if (method === 'progress' && progress != null && progress !== '') ev_amount = budget * (parseFloat(progress) / 100)
    if (method === 'qty_rate' && qty != null && rate != null && qty !== '' && rate !== '') ev_amount = parseFloat(qty) * parseFloat(rate)
    if (method === 'manual' && manual != null && manual !== '') ev_amount = parseFloat(manual)
    setEvSaving(p => ({ ...p, [item.id]: true }))
    const { error } = await supabase.from('budget_summary').update({
      ev_method:   method,
      ev_progress: method === 'progress' ? (parseFloat(progress) || null) : null,
      ev_qty:      method === 'qty_rate' ? (parseFloat(qty)      || null) : null,
      ev_rate:     method === 'qty_rate' ? (parseFloat(rate)     || null) : null,
      ev_amount,
    }).eq('id', item.id)
    if (error) showToast('فشل: ' + error.message, 'error')
    else showToast('تم الحفظ', 'success')
    setEvSaving(p => ({ ...p, [item.id]: false }))
  }

  function getEvVal(item, key) { return evLocal[item.id]?.[key] !== undefined ? evLocal[item.id][key] : item[key] }
  function setEvField(itemId, key, val) { setEvLocal(p => ({ ...p, [itemId]: { ...p[itemId], [key]: val } }) ) }
  function calcEV(item) {
    const method   = getEvVal(item, 'ev_method')   ?? 'progress'
    const progress = getEvVal(item, 'ev_progress')
    const qty      = getEvVal(item, 'ev_qty')
    const rate     = getEvVal(item, 'ev_rate')
    const budget   = parseFloat(item.total_budget) || 0
    if (method === 'progress' && progress != null && progress !== '') return budget * (parseFloat(progress) / 100)
    if (method === 'qty_rate' && qty != null && rate != null && qty !== '' && rate !== '') return parseFloat(qty) * parseFloat(rate)
    return item.ev_amount ?? null
  }

  return (
    <div style={{minHeight:"100vh",background:"#F3F4F6",fontFamily:"inherit"}}>
      {/* Header */}
      <div style={{background:"#fff",borderBottom:"1px solid #E5E7EB",padding:"18px 24px 14px"}}>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:16}}>
          <div>
            <h1 className="text-xl font-extrabold text-[#0F1017] tracking-tight">{activeTab === 'direct' ? 'Direct Budget' : 'Tender Direct'}</h1>
            <p className="text-xs text-[#6B7280] mt-0.5">{activeTab === 'direct' ? 'ميزانية التكاليف المباشرة حسب Package' : 'تحليل الـ Tender — مواد / مقاولين / معدات'}</p>
          </div>
          <div className="flex items-center gap-1 bg-[#F3F4F6] border border-[#E5E7EB] rounded-xl p-1">
            {[{id:'direct',label:'📋 Budget (Direct)'},{id:'tender',label:'📊 Tender Direct'}].map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTab===t.id ? 'bg-[#0F1017] text-white' : 'text-[#6B7280] hover:bg-[#FAFAFA]'}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {can('Budget.Edit') && (
              <button onClick={() => {
                if (activeTab === 'tender') {
                  setTenderAddForm({ item_number:'', description:'', cost_code:'', package_name:'', qty:'', unit:'', unit_rate:'', mat_code:'', mat_qty:'', mat_unit:'', mat_rate:'', mat_total:'', lab_code:'', lab_qty:'', lab_unit:'', lab_rate:'', lab_total:'', sc_code:'', sc_qty:'', sc_unit:'', sc_rate:'', sc_total:'', eqp_code:'', eqp_qty:'', eqp_unit:'', eqp_rate:'', eqp_total:'' })
                  setShowTenderAddModal(true)
                } else {
                  setEditItem(null); setAddForm({ package_name:'', cost_code:'', sub_code:'', description:'', total_budget:'', row_type:'' }); setShowAddModal(true)
                }
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm text-white bg-[#1A7A4A] rounded-lg hover:bg-[#15693f]">
              + Add Row
            </button>
            )}
            <button onClick={downloadTemplate}
              className="flex items-center gap-2 px-3 py-2 text-xs text-[#6B7280] border border-[#E5E7EB] rounded-lg hover:bg-[#FAFAFA]">
              <DownloadIcon /> Template
            </button>
            <button onClick={exportData}
              className="flex items-center gap-2 px-3 py-2 text-sm text-white bg-[#1A7A4A] rounded-lg hover:bg-[#15623B]">
              <DownloadIcon /> Export
            </button>
          </div>
        </div>
      </div>

      <div style={{padding:"20px 24px",display:"flex",flexDirection:"column",gap:16}}>
        {tenderUploadMode === 'asking' && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm">
              <h3 className="font-bold text-[#0F1017] text-base mb-2">Upload {tenderUploadPreview?.length} rows</h3>
              <p className="text-sm text-[#6B7280] mb-5">Do you want to replace existing data or append to it?</p>
              <div className="flex flex-col gap-2">
                {can('Budget.Edit') && (
                  <button onClick={() => confirmTenderUpload('replace')}
                    className="w-full px-4 py-2.5 bg-red-600 text-white text-sm font-medium rounded-xl hover:bg-red-700">
                    🔄 Replace — Delete existing & upload new
                  </button>
                )}
                {can('Budget.Edit') && (
                  <button onClick={() => confirmTenderUpload('append')}
                    className="w-full px-4 py-2.5 bg-[#1A7A4A] text-white text-sm font-medium rounded-xl hover:bg-[#15693f]">
                    ➕ Append — Add to existing data
                  </button>
                )}
                <button onClick={() => setTenderUploadMode(null)}
                  className="w-full px-4 py-2 text-sm text-[#6B7280] border border-[#E5E7EB] rounded-xl hover:bg-[#FAFAFA]">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Tender Add Modal */}
        {showTenderAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-5 border-b border-[#E5E7EB]">
                <h2 className="text-lg font-bold text-[#0F1017]">إضافة Activity جديدة</h2>
                <button onClick={() => setShowTenderAddModal(false)} className="text-[#9CA3AF] hover:text-[#6B7280] text-xl">×</button>
              </div>
              <div className="p-5 space-y-4">
                {/* Activity info */}
                <div className="grid grid-cols-2 gap-3">
                  {[{k:'item_number',l:'Item No.'},{k:'package_name',l:'Package'},{k:'cost_code',l:'Cost Code'},{k:'unit',l:'Unit'}].map(f=>(
                    <div key={f.k}>
                      <label className="text-xs text-[#6B7280] mb-1 block">{f.l}</label>
                      <input value={tenderAddForm[f.k]} onChange={e=>setTenderAddForm(p=>({...p,[f.k]:e.target.value}))}
                        className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]/30"/>
                    </div>
                  ))}
                </div>
                <div>
                  <label className="text-xs text-[#6B7280] mb-1 block">Description *</label>
                  <input value={tenderAddForm.description} onChange={e=>setTenderAddForm(p=>({...p,description:e.target.value}))}
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]/30"/>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#6B7280] mb-1 block">Activity Qty</label>
                    <input type="number" value={tenderAddForm.qty} onChange={e=>setTenderAddForm(p=>({...p,qty:e.target.value}))}
                      className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]/30"/>
                  </div>
                  <div>
                    <label className="text-xs text-[#6B7280] mb-1 block">Total Budget (auto)</label>
                    <div className="w-full border border-[#E5E7EB] bg-[#F9FAFB] rounded-lg px-3 py-2 text-sm font-mono text-[#0F1017]">
                      {Number(['mat','lab','sc','eqp'].reduce((s,k)=>s+(parseFloat(tenderAddForm[`${k}_total`])||0),0)).toLocaleString('en-US',{minimumFractionDigits:2})}
                    </div>
                  </div>
                </div>
                {/* Resource rows */}
                {[
                  {key:'mat', label:'Material',    color:'text-blue-700',   bg:'bg-blue-50',   codeKey:'mat_code'},
                  {key:'lab', label:'Labour',      color:'text-green-700',  bg:'bg-green-50',  codeKey:'lab_code'},
                  {key:'sc',  label:'Subcontract', color:'text-orange-700', bg:'bg-orange-50', codeKey:'sc_code'},
                  {key:'eqp', label:'Equipment',   color:'text-amber-700',  bg:'bg-amber-50',  codeKey:'eqp_code'},
                ].map(r => (
                  <div key={r.key} className={`${r.bg} rounded-xl p-3`}>
                    <p className={`text-xs font-bold ${r.color} mb-2`}>{r.label}</p>
                    <div className="grid grid-cols-6 gap-2">
                      {[
                        {k:`${r.key}_code`,  l:'Sub Code'},
                        {k:`${r.key}_desc`,  l:'Description (e.g. pumps)'},
                        {k:`${r.key}_qty`,   l:'Qty',  type:'number'},
                        {k:`${r.key}_unit`,  l:'Unit'},
                        {k:`${r.key}_rate`,  l:'Rate', type:'number'},
                        {k:`${r.key}_total`, l:'Total',type:'number'},
                      ].map(f=>(
                        <div key={f.k}>
                          <label className="text-xs text-[#6B7280] mb-1 block">{f.l}</label>
                          {f.k.endsWith('_code') ? (
                            <>
                              <input list={`add-sc-${f.k}`} type="text" value={tenderAddForm[f.k]} onChange={e=>setTenderAddForm(p=>({...p,[f.k]:e.target.value}))}
                                className="w-full border border-white rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]/30" placeholder="sub code"/>
                              <datalist id={`add-sc-${f.k}`}>
                                {costCodes.filter(c=>c.level===2||c.parent_id).map(sc=><option key={sc.id} value={sc.code}>{sc.code} — {sc.description}</option>)}
                              </datalist>
                            </>
                          ) : (
                            <input type={f.type||'text'} value={tenderAddForm[f.k]} onChange={e=>setTenderAddForm(p=>({...p,[f.k]:e.target.value}))}
                              className="w-full border border-white rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]/30"/>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
                <button onClick={() => setShowTenderAddModal(false)} className="flex-1 py-2 border border-[#E5E7EB] rounded-lg text-sm hover:bg-[#FAFAFA]">Cancel</button>
                {can('Budget.Edit') && (
                  <button onClick={handleSaveTenderActivity} className="flex-1 py-2 bg-[#1A7A4A] text-white rounded-lg text-sm font-medium hover:bg-[#15693f]">Add Activity</button>
                )}
              </div>
            </div>
          </div>
        )}
        {activeTab === 'tender' && (
          <TenderDirectTab
            projectId={selectedProject?.id}
            items={tenderItems}
            loading={tenderLoading}
            uploadPreview={tenderUploadPreview}
            uploading={tenderUploading}
            onFileSelect={async (file) => { const rows = await parseTenderExcel(file); setTenderUploadPreview(rows) }}
            onConfirmUpload={() => setTenderUploadMode('asking')}
            onCancelUpload={() => { setTenderUploadPreview(null); setTenderUploadMode(null) }}
            selected={tenderSelected}
            onToggle={(id) => setTenderSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })}
            onToggleAll={() => setTenderSelected(s => s.size === tenderItems.filter(i=>!i.is_package_header).length ? new Set() : new Set(tenderItems.filter(i=>!i.is_package_header).map(i=>i.id)))}
            onDeleteSelected={deleteTenderSelected}
            viewMode={tenderViewMode}
            onToggleView={() => setTenderViewMode(v => v === 'table' ? 'packages' : 'table')}
            editItem={tenderEditItem}
            editForm={tenderEditForm}
            onEdit={(item) => { setTenderEditItem(item); setTenderEditForm({...item}) }}
            onEditChange={(key, val) => setTenderEditForm(f => ({...f, [key]: val}))}
            onEditSave={saveTenderEdit}
            onEditCancel={() => setTenderEditItem(null)}
            analysisCodes={analysisCodes}
            subCodes={costCodes.filter(c => c.level === 2 || c.parent_id)}
            can={can}
          />
        )}
        {activeTab === 'direct' && (<>

        {/* Project Selector — only when no project */}
        {!selectedProject ? (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
            <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-3">اختر المشروع</label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {projects.map(p => (
                <button key={p.id} onClick={() => handleProjectSelect(p)}
                  className="text-left px-4 py-3 rounded-xl border border-[#E5E7EB] hover:border-[#1A7A4A]/50 hover:bg-[#FAFAFA] transition-all">
                  <p className="text-sm font-semibold text-[#0F1017]">{p.name}</p>
                  <p className="text-xs text-[#9CA3AF]">{p.organizations?.name}</p>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm">
            <span className="font-semibold text-[#2E2C29]">{selectedProject.name}</span>
            <button onClick={() => setSelectedProject(null)} className="text-xs text-blue-500 hover:underline">تغيير</button>
          </div>
        )}

        {selectedProject && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                <div className="text-xl font-extrabold text-[#0F1017] tracking-tight">{directBudgetRows.length}</div>
                <div className="text-xs text-[#6B7280] mt-1">Cost Codes</div>
              </div>
              <div className="bg-[#0F1017] rounded-xl p-4">
                <div className="text-lg font-bold text-white">{fmt(directTotalBudget)}</div>
                <div className="text-xs text-[#9CA3AF] mt-1">إجمالي الميزانية المباشرة</div>
              </div>
              <div className="bg-[#1A7A4A] rounded-xl p-4">
                <div className="text-xs font-semibold text-white/60 uppercase tracking-wide mb-1">Total Earned Value</div>
                <div className="text-lg font-bold text-white">{fmt(totalDirectEV)}</div>
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-white rounded-full" style={{width:`${Math.min(totalDirectEVPct,100)}%`}}/>
                  </div>
                  <span className="text-xs font-bold text-white">{totalDirectEVPct.toFixed(1)}%</span>
                </div>
                <div className="text-xs text-white/50 mt-1">of Direct Budget</div>
              </div>
            </div>

            {/* Direct Budget Table — from Tender, now grouped by Resource Code under each Cost Code */}
            {tenderLoading ? (
              <div className="text-center py-16 text-[#9CA3AF] text-sm">Loading...</div>
            ) : directBudgetRows.length === 0 ? (
              <div className="text-center py-16 text-[#9CA3AF] text-sm">لا يوجد بيانات — ارفع Excel في تاب Tender أولاً</div>
            ) : (
              <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Cost Code / Resource Code</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Description</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Package</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Total Budget</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-purple-600 uppercase">Progress</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-[#1A7A4A] uppercase">Earned Value</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6]">
                      {directBudgetRows.map((ccRow, idx) => {
                        const ccObj = costCodes.find(c => c.code?.trim() === ccRow.cost_code?.trim())
                        const ccDesc = ccObj?.description || ccRow.resources[0]?.items[0]?.description || '—'
                        const ccEvAmt = calcCostCodeEv(ccRow)
                        const ccEvPct = ccRow.total_budget > 0 ? (ccEvAmt / ccRow.total_budget * 100) : 0
                        return (
                          <Fragment key={idx}>
                            {/* Cost Code rollup row — budget & EV are pure sums of its Resource Codes below */}
                            <tr className="bg-[#F3F4F6] font-semibold">
                              <td className="px-4 py-2.5 font-mono text-xs text-[#1A7A4A] font-bold">{ccRow.cost_code || '—'}</td>
                              <td className="px-4 py-2.5 text-sm text-[#0F1017] font-semibold">{ccDesc}</td>
                              <td className="px-4 py-2.5 text-xs text-[#6B7280]">{ccRow.package_name || '—'}</td>
                              <td className="px-4 py-2.5 text-right font-bold font-mono text-xs text-[#0F1017]">{fmtN(ccRow.total_budget)}</td>
                              <td className="px-4 py-2.5 text-center text-xs text-[#6B7280]">{ccEvPct.toFixed(0)}%</td>
                              <td className="px-4 py-2.5 text-right font-bold font-mono text-xs text-[#1A7A4A]">{ccEvAmt > 0 ? fmtN(ccEvAmt) : '—'}</td>
                              <td className="px-4 py-2.5"></td>
                            </tr>
                            {/* Resource Code detail rows — budget and Progress/EV are entered here */}
                            {ccRow.resources.map((row, ri) => {
                              const ev = directEv[row.resource_code] || {}
                              const evAmt = calcResourceEv(row)
                              return (
                                <tr key={ri} className="hover:bg-[#FAFAFA]">
                                  <td className="px-4 py-2 pl-8 font-mono text-xs text-[#6B7280]">↳ {row.resource_code || '—'}</td>
                                  <td className="px-4 py-2 text-xs text-[#374151]">{row.resource_description || '—'}</td>
                                  <td className="px-4 py-2 text-xs text-[#9CA3AF]">{row.package_name || '—'}</td>
                                  <td className="px-4 py-2 text-right font-mono text-xs text-[#6B7280]">{fmtN(row.total_budget)}</td>
                                  <td className="px-4 py-2 text-center text-xs text-[#6B7280]">
                                    {ev.ev_method === 'progress' ? `${ev.ev_progress ?? '—'}%`
                                     : ev.ev_method === 'qty_rate' ? `${ev.ev_qty ?? '—'} × ${ev.ev_rate ?? '—'}`
                                     : ev.ev_method === 'manual' ? 'Manual'
                                     : '—'}
                                  </td>
                                  <td className="px-4 py-2 text-right font-mono text-xs text-[#1A7A4A]">{evAmt > 0 ? fmtN(evAmt) : '—'}</td>
                                  <td className="px-4 py-2 text-center">
                                    {row.resource_code ? (
                                      <button onClick={() => { setDirectEvModal(row); setDirectEvLocal(ev) }}
                                        className="px-2 py-1 text-xs border border-purple-300 text-purple-700 rounded hover:bg-purple-50">
                                        {ev.ev_method ? '✏️ Edit EV' : '+ EV'}
                                      </button>
                                    ) : (
                                      <span className="text-xs text-[#D1D5DB] italic">no code</span>
                                    )}
                                  </td>
                                </tr>
                              )
                            })}
                          </Fragment>
                        )
                      })}
                    </tbody>
                    <tfoot className="bg-[#0F1017]">
                      <tr>
                        <td colSpan={3} className="px-4 py-3 text-xs font-bold text-slate-400 uppercase">Grand Total</td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-white font-mono">{fmtN(directTotalBudget)}</td>
                        <td/>
                        <td className="px-4 py-3 text-right text-sm font-bold text-[#2FA866] font-mono">{fmtN(totalDirectEV)}</td>
                        <td/>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
        </>)}
      </div>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* Direct Budget EV Modal */}
      {directEvModal && (() => {
        const row = directEvModal
        const local = directEvLocal
        const method = local.ev_method || 'progress'
        const budget = row.total_budget || 0
        const previewEV = () => {
          if (method === 'progress' && local.ev_progress != null && local.ev_progress !== '') return budget * (parseFloat(local.ev_progress) / 100)
          if (method === 'qty_rate' && local.ev_qty != null && local.ev_rate != null) return parseFloat(local.ev_qty) * parseFloat(local.ev_rate)
          if (method === 'manual' && local.ev_manual != null && local.ev_manual !== '') return parseFloat(local.ev_manual)
          return null
        }
        const ev = previewEV()
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#F3F4F6] rounded-2xl shadow-2xl w-full max-w-md">
              <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
                <div>
                  <h2 className="text-base font-bold text-[#0F1017]">Earned Value</h2>
                  <p className="text-xs text-[#9CA3AF] mt-0.5">{row.resource_code} — {row.resource_description || row.package_name}</p>
                </div>
                <button onClick={() => setDirectEvModal(null)} className="text-[#9CA3AF] hover:text-[#6B7280] text-xl">×</button>
              </div>
              <div className="p-5 space-y-4">
                <div className="bg-white rounded-xl px-4 py-3 flex items-center justify-between">
                  <span className="text-xs text-[#9CA3AF] uppercase tracking-wide">Budget</span>
                  <span className="font-bold text-[#1A7A4A]">{fmtN(budget)}</span>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Method</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[{v:'progress',l:'Progress %'},{v:'qty_rate',l:'Qty × Rate'},{v:'manual',l:'Manual'}].map(m => (
                      <button key={m.v} onClick={() => setDirectEvLocal(p => ({...p, ev_method: m.v}))}
                        className={`py-2 rounded-lg text-xs font-medium border transition-colors ${method===m.v ? 'bg-purple-600 text-white border-purple-600' : 'border-[#E5E7EB] text-[#6B7280] hover:bg-[#FAFAFA]'}`}>
                        {m.l}
                      </button>
                    ))}
                  </div>
                </div>
                {method === 'progress' && (
                  <div>
                    <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Progress %</label>
                    <input type="number" min="0" max="100" step="0.5" placeholder="0 – 100"
                      value={local.ev_progress ?? ''}
                      onChange={e => setDirectEvLocal(p => ({...p, ev_progress: e.target.value}))}
                      className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"/>
                  </div>
                )}
                {method === 'qty_rate' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Qty</label>
                      <input type="number" placeholder="Quantity" value={local.ev_qty ?? ''}
                        onChange={e => setDirectEvLocal(p => ({...p, ev_qty: e.target.value}))}
                        className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"/>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Rate</label>
                      <input type="number" placeholder="Rate" value={local.ev_rate ?? ''}
                        onChange={e => setDirectEvLocal(p => ({...p, ev_rate: e.target.value}))}
                        className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"/>
                    </div>
                  </div>
                )}
                {method === 'manual' && (
                  <div>
                    <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">EV Amount</label>
                    <input type="number" placeholder="Enter EV directly" value={local.ev_manual ?? ''}
                      onChange={e => setDirectEvLocal(p => ({...p, ev_manual: e.target.value}))}
                      className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"/>
                  </div>
                )}
                {ev != null && (
                  <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3 flex items-center justify-between">
                    <span className="text-xs text-purple-600 font-semibold uppercase tracking-wide">EV Preview</span>
                    <span className="text-lg font-black text-purple-700">{fmtN(ev)}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
                <button onClick={() => setDirectEvModal(null)}
                  className="flex-1 py-2 border border-[#E5E7EB] rounded-lg text-sm hover:bg-[#FAFAFA]">Cancel</button>
                <button onClick={async () => {
                  const evMethod = local.ev_method || 'progress'
                  const evProgress = local.ev_progress != null ? parseFloat(local.ev_progress) : null
                  const evQty = local.ev_qty != null ? parseFloat(local.ev_qty) : null
                  const evRate = local.ev_rate != null ? parseFloat(local.ev_rate) : null
                  const evManual = local.ev_manual != null ? parseFloat(local.ev_manual) : null
                  // Persist the computed EV amount (in currency) alongside the raw inputs —
                  // same formula as calcResourceEv/previewEV above, computed once at save time
                  // so downstream consumers (Cost Report shared service) can read a ready number
                  // instead of re-deriving it from method/progress/qty/rate on every read.
                  let evAmount = 0
                  if (evMethod === 'progress' && evProgress != null) evAmount = budget * (evProgress / 100)
                  else if (evMethod === 'qty_rate' && evQty != null && evRate != null) evAmount = evQty * evRate
                  else if (evMethod === 'manual' && evManual != null) evAmount = evManual
                  await saveDirectEv(row.resource_code, {
                    ev_method: evMethod,
                    ev_progress: evProgress,
                    ev_qty: evQty,
                    ev_rate: evRate,
                    ev_manual: evManual,
                    ev_amount: evAmount,
                  })
                  setDirectEvModal(null)
                }} className="flex-1 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">
                  Save EV
                </button>
              </div>
            </div>
          </div>
        )
      })()} 

      {/* EV Modal */}
      {evModal && (() => {
        const item = evModal
        const method   = getEvVal(item,'ev_method')   ?? 'progress'
        const progress = getEvVal(item,'ev_progress')  ?? ''
        const qty      = getEvVal(item,'ev_qty')       ?? ''
        const rate     = getEvVal(item,'ev_rate')      ?? ''
        const manual   = getEvVal(item,'ev_manual')    ?? ''
        const budget   = parseFloat(item.total_budget) || 0
        const previewEV = () => {
          if (method==='progress' && progress!=='') return budget*(parseFloat(progress)/100)
          if (method==='qty_rate' && qty!=='' && rate!=='') return parseFloat(qty)*parseFloat(rate)
          if (method==='manual' && manual!=='') return parseFloat(manual)
          return null
        }
        const ev = previewEV()
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#F3F4F6] rounded-2xl shadow-2xl w-full max-w-md">
              <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
                <div>
                  <h2 className="text-base font-bold text-[#0F1017]">Earned Value</h2>
                  <p className="text-xs text-[#9CA3AF] mt-0.5 truncate max-w-xs">{item.description}</p>
                </div>
                <button onClick={() => { setEvModal(null); setEvLocal(p => { const n={...p}; delete n[item.id]; return n }) }}
                  className="text-[#9CA3AF] hover:text-[#6B7280] text-xl">×</button>
              </div>
              <div className="p-5 space-y-4">
                {/* Budget info */}
                <div className="bg-white rounded-xl px-4 py-3 flex items-center justify-between">
                  <span className="text-xs text-[#9CA3AF] uppercase tracking-wide">Budget</span>
                  <span className="font-bold text-[#1A7A4A]">{fmtN(budget)}</span>
                </div>
                {/* Method selector */}
                <div>
                  <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Method</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[{v:'progress',l:'Progress %'},{v:'qty_rate',l:'Qty × Rate'},{v:'manual',l:'Manual'}].map(m => (
                      <button key={m.v} onClick={() => setEvField(item.id,'ev_method',m.v)}
                        className={`py-2 rounded-lg text-xs font-medium border transition-colors ${method===m.v ? 'bg-purple-600 text-white border-purple-600' : 'border-[#E5E7EB] text-[#6B7280] hover:bg-[#FAFAFA]'}`}>
                        {m.l}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Inputs */}
                {method==='progress' && (
                  <div>
                    <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Progress %</label>
                    <input type="number" min="0" max="100" step="0.5" placeholder="0 – 100"
                      value={progress}
                      onChange={e => setEvField(item.id,'ev_progress',e.target.value)}
                      className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"/>
                  </div>
                )}
                {method==='qty_rate' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Qty</label>
                      <input type="number" placeholder="Quantity" value={qty}
                        onChange={e => setEvField(item.id,'ev_qty',e.target.value)}
                        className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"/>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Rate</label>
                      <input type="number" placeholder="Rate" value={rate}
                        onChange={e => setEvField(item.id,'ev_rate',e.target.value)}
                        className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"/>
                    </div>
                  </div>
                )}
                {method==='manual' && (
                  <div>
                    <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">EV Amount</label>
                    <input type="number" placeholder="Enter EV directly" value={manual}
                      onChange={e => setEvField(item.id,'ev_manual',e.target.value)}
                      className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"/>
                  </div>
                )}
                {/* Preview */}
                {ev != null && (
                  <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3 flex items-center justify-between">
                    <span className="text-xs text-purple-600 font-semibold uppercase tracking-wide">EV Preview</span>
                    <span className="text-lg font-black text-purple-700">{fmtN(ev)}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
                <button onClick={() => { setEvModal(null); setEvLocal(p => { const n={...p}; delete n[item.id]; return n }) }}
                  className="flex-1 py-2 border border-[#E5E7EB] rounded-lg text-sm hover:bg-[#FAFAFA]">Cancel</button>
                {can('Budget.Edit') && (
                  <button onClick={async () => {
                  const evItem = { ...item, ev_method: method, ev_progress: progress||null, ev_qty: qty||null, ev_rate: rate||null, ev_manual: manual||null }
                  // override ev_amount for manual
                  if (method==='manual') evItem._manualAmount = parseFloat(manual)||null
                  await saveEV(evItem)
                  // update items state with new ev_amount
                  setItems(prev => prev.map(i => {
                    if (i.id !== item.id) return i
                    const ea = method==='manual' ? (parseFloat(manual)||null) : (ev ?? i.ev_amount)
                    return { ...i, ev_method: method, ev_progress: progress||null, ev_qty: qty||null, ev_rate: rate||null, ev_amount: ea }
                  }))
                  setEvModal(null)
                }} disabled={evSaving[item.id]}
                  className="flex-1 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50">
                  {evSaving[item.id] ? 'Saving...' : 'Save EV'}
                </button>
                )}
              </div>
            </div>
          </div>
        )
      })()}

      {/* Add/Edit Row Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#F3F4F6] rounded-2xl shadow-2xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
              <h2 className="text-lg font-bold text-[#0F1017]">{editItem ? 'تعديل سطر' : 'إضافة سطر جديد'}</h2>
              <button onClick={() => setShowAddModal(false)} className="text-[#9CA3AF] hover:text-[#6B7280] text-xl">×</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-[#6B7280] mb-1">Package Name</label>
                  <select value={addForm.package_name} onChange={e => setAddForm(p=>({...p, package_name:e.target.value}))}
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]">
                    <option value="">-- اختر Package --</option>
                    {uniquePackages.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[#6B7280] mb-1">Cost Code</label>
                  <select value={addForm.cost_code} onChange={e => setAddForm(p=>({...p, cost_code:e.target.value, sub_code:''}))}
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]">
                    <option value="">-- اختر --</option>
                    {costCodes.filter(c => c.level === 1 || c.level === '1').map(c=>(
                      <option key={c.id} value={c.code}>{c.code} — {c.description}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-[#6B7280] mb-1">Sub Code</label>
                  <select value={addForm.sub_code} onChange={e => setAddForm(p=>({...p, sub_code:e.target.value}))}
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]">
                    <option value="">-- اختر --</option>
                    {costCodes.filter(c => (c.level === 2 || c.level === '2') && costCodes.find(p => p.id === c.parent_id && p.code === addForm.cost_code)).map(c=>(
                      <option key={c.id} value={c.code}>{c.code} — {c.description}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[#6B7280] mb-1">Row Type</label>
                  <select value={addForm.row_type} onChange={e => setAddForm(p=>({...p, row_type:e.target.value}))}
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]">
                    <option value="">Sub Row</option>
                    <option value="total">Total Row</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Description *</label>
                <input value={addForm.description} onChange={e => setAddForm(p=>({...p, description:e.target.value}))}
                  className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]" />
              </div>
              <div>
                <label className="block text-xs text-[#6B7280] mb-1">Total Budget</label>
                {addForm.included
                  ? <div className="w-full border border-amber-300 bg-amber-50 rounded-lg px-3 py-2 text-sm text-amber-600 italic">included</div>
                  : <input value={addForm.total_budget} onChange={e => setAddForm(p=>({...p, total_budget:e.target.value}))}
                      placeholder="e.g. 500000"
                      className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A7A4A]" />
                }
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={!!addForm.included} onChange={e => setAddForm(p=>({...p, included: e.target.checked, total_budget: ''}))}
                  className="w-4 h-4 text-amber-500 rounded"/>
                <span className="text-xs text-[#6B7280]">Included in package total (no separate budget)</span>
              </label>
            </div>
            <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
              <button onClick={() => setShowAddModal(false)} className="flex-1 py-2 border border-[#E5E7EB] rounded-lg text-sm hover:bg-[#FAFAFA]">Cancel</button>
              {can('Budget.Edit') && (
                <button onClick={handleSaveItem} className="flex-1 py-2 bg-[#0F1017] text-white rounded-lg text-sm font-medium hover:bg-[#2E2C29]">
                  {editItem ? 'Save Changes' : 'Add Row'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
